// Enhanced Firebase service with proper authentication
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, query, where, orderBy, getDocs, limit, doc, setDoc, getDoc, addDoc, updateDoc, serverTimestamp, deleteDoc, onSnapshot } from 'firebase/firestore';
import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    updateProfile,
    sendPasswordResetEmail
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Your existing Firebase config
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
let app;
let db;
let auth;
let storage;
let functions;

try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    storage = getStorage(app);
    functions = getFunctions(app, 'us-central1');

    console.log('🔥 Firebase initialized successfully with project:', firebaseConfig.projectId);
} catch (error) {
    console.error('❌ Firebase initialization error:', error);
}

// Auth state management
let currentUser = null;
let authStateResolve = null;
const authStatePromise = new Promise(resolve => {
    authStateResolve = resolve;
});

onAuthStateChanged(auth, (user) => {
    currentUser = user;
    if (authStateResolve) {
        authStateResolve(user);
        authStateResolve = null;
    }
    console.log('👤 Auth state changed:', user ? `Logged in as ${user.email}` : 'Not logged in');
});

// Wait for auth state to be determined
export const waitForAuthState = () => authStatePromise;

// Get current user ID
const getCurrentUserId = () => {
    return currentUser?.uid || null;
};

// Callables send Authorization from `auth.currentUser` only. Prefer this over the module-level
// `currentUser` (can lag right after sign-in) so the Functions SDK always attaches a token.
const getCallableUser = async () => {
    if (!auth) {
        return null;
    }
    await auth.authStateReady();
    const u = auth.currentUser;
    if (!u) {
        return null;
    }
    await u.getIdToken();
    return u;
};

// Ensure user is authenticated and token is fresh
export const ensureAuthenticated = async () => {
    if (!currentUser) {
        throw new Error('User not authenticated');
    }
    
    // Force token refresh to ensure it's valid
    try {
        await currentUser.getIdToken(true);
        console.log('✅ Authentication token refreshed');
    } catch (error) {
        console.error('❌ Token refresh failed:', error);
        throw new Error('Authentication token expired. Please sign in again.');
    }
    
    return currentUser;
};

// Authentication functions
export const signInWithEmail = async (email, password) => {
    try {
        console.log('🔐 Signing in with email:', email);
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Sign in successful');
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('❌ Sign in error:', error);
        return { success: false, error: error.message };
    }
};

export const signUpWithEmail = async (email, password, displayName) => {
    try {
        console.log('📝 Creating account for:', email);
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Update user profile with display name
        if (displayName) {
            await updateProfile(userCredential.user, { displayName });
        }

        console.log('✅ Account created successfully');
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('❌ Sign up error:', error);
        return { success: false, error: error.message };
    }
};

export const signInWithGoogle = async () => {
    try {
        console.log('🔐 Signing in with Google');
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        console.log('✅ Google sign in successful');
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('❌ Google sign in error:', error);
        return { success: false, error: error.message };
    }
};

export const signOutUser = async () => {
    try {
        console.log('👋 Signing out');
        await signOut(auth);
        console.log('✅ Sign out successful');
        return { success: true };
    } catch (error) {
        console.error('❌ Sign out error:', error);
        return { success: false, error: error.message };
    }
};

export const getCurrentUser = () => currentUser;

// Firebase Functions with proper authentication
export const searchKnowledgeBase = async (data) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('🔍 Calling searchKnowledgeBase function:', { ...data, userId });
        const fn = httpsCallable(functions, 'searchKnowledgeBase');
        const result = await fn({ ...data, userId });
        console.log('✅ Search result:', result.data);
        return result.data;
    } catch (error) {
        console.error('❌ Search knowledge base error:', error);
        return {
            success: false,
            error: error.message,
            response: 'Please sign in to search your knowledge base.',
            relevantRecordings: [],
            relevantDocuments: []
        };
    }
};

export const transcribeAudioAPI = async (audioBlob, category, duration) => {
    try {
        // Wait for auth state to be determined
        await waitForAuthState();
        
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated to transcribe audio');
        }
        
        // Force token refresh to ensure we have a valid token
        if (currentUser) {
            await currentUser.getIdToken(true);
        }

        console.log('🎵 Starting transcription process:', { category, duration, userId });

        // Generate a unique filename
        const timestamp = Date.now();
        const audioFileName = `recordings/${userId}/${timestamp}_${category}.webm`;

        // Upload to Firebase Storage
        console.log('📤 Uploading audio to Firebase Storage...');
        const audioRef = ref(storage, audioFileName);
        const uploadResult = await uploadBytes(audioRef, audioBlob);
        const audioUrl = await getDownloadURL(uploadResult.ref);

        console.log('✅ Audio uploaded successfully:', audioUrl);

        // Call transcription function with the correct parameters
        console.log('🤖 Calling transcription function...');
        console.log('🔐 Current user:', currentUser?.uid, currentUser?.email);
        console.log('🔐 Auth token exists:', !!currentUser?.accessToken);
        
        const fn = httpsCallable(functions, 'transcribeAudio');

        // Fixed: Pass the storage path, not the download URL
        const result = await fn({
            audioUrl: audioFileName, // This should be the storage path
            projectId: 'default',     // Project ID
            userId: userId            // Explicit user ID for temporary fix
        });

        console.log('✅ Transcription result:', result.data);
        return {
            success: true,
            recordingId: result.data.transcriptionId || timestamp,
            transcript: result.data.transcription,
            insights: result.data.insights || {},
            audioUrl: audioUrl
        };
    } catch (error) {
        console.error('❌ Transcribe audio error:', error);

        // Enhanced fallback with better error handling
        const timestamp = Date.now();
        const fallbackInsights = {
            topics: [`${category.charAt(0).toUpperCase() + category.slice(1)} Discussion`, 'Key Points', 'Action Items'],
            actionPoints: [
                {
                    text: `Follow up on ${category} discussion points`,
                    priority: 'medium',
                    assignee: 'Team Member',
                    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                },
                {
                    text: `Review and document ${category} outcomes`,
                    priority: 'low',
                    assignee: 'Team Member',
                    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                }
            ],
            summary: `This ${category} recording lasted ${Math.floor(duration / 60)} minutes and ${duration % 60} seconds.`,
            meetingNotes: {
                summary: `Discussion captured from your ${category} recording. Review the transcript for full detail.`,
                nextSteps: ['Review action items', 'Share notes with your team']
            },
            decisions: [`Important ${category} decisions were captured`],
            followUps: ['Schedule follow-up discussion', 'Review action items', 'Share key insights with team']
        };

        return {
            success: false,
            recordingId: timestamp,
            transcript: `Demo transcript for ${category} recording (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}). This is a demonstration of how the AI transcription would work. The actual system would process your audio and provide detailed transcription with speaker identification, timestamps, and key insight extraction. Important topics, decisions, and action items would be automatically identified and organized for easy review.`,
            insights: fallbackInsights,
            audioUrl: URL.createObjectURL(audioBlob), // Use local URL as fallback
            error: error.message
        };
    }
};

// Save functions with user authentication
export const saveRecordingToFirestore = async (recording) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('💾 Saving recording to Firestore:', recording.id);

        // Generate a proper Firestore document ID
        const recordingRef = doc(collection(db, 'recordings'));
        const firestoreId = recordingRef.id;
        
        await setDoc(recordingRef, {
            ...recording,
            id: firestoreId, // Update the ID to the Firestore document ID
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('✅ Recording saved to Firestore successfully');
        // Return the updated recording with the Firestore ID
        return { success: true, recordingId: firestoreId };
    } catch (error) {
        console.error('❌ Error saving recording to Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const updateUserDisplayName = async (displayName) => {
    try {
        await waitForAuthState();
        if (!currentUser) {
            throw new Error('User must be authenticated');
        }
        await updateProfile(currentUser, { displayName: displayName || '' });
        return { success: true };
    } catch (error) {
        console.error('❌ updateUserDisplayName error:', error);
        return { success: false, error: error.message };
    }
};

export const sendPasswordReset = async (email) => {
    try {
        if (!email) {
            throw new Error('Email is required');
        }
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error) {
        console.error('❌ sendPasswordReset error:', error);
        return { success: false, error: error.message };
    }
};

export const saveDocumentToFirestore = async (document) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('💾 Saving document to Firestore:', document.id);

        // Generate a proper Firestore document ID
        const documentRef = doc(collection(db, 'documents'));
        const firestoreId = documentRef.id;
        
        await setDoc(documentRef, {
            ...document,
            id: firestoreId, // Update the ID to the Firestore document ID
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('✅ Document saved to Firestore successfully');
        // Return the updated document with the Firestore ID
        return { success: true, documentId: firestoreId };
    } catch (error) {
        console.error('❌ Error saving document to Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const saveTaskToFirestore = async (task) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('💾 Saving task to Firestore:', task.id);

        // Generate a proper Firestore document ID
        const taskRef = doc(collection(db, 'tasks'));
        const firestoreId = taskRef.id;
        
        await setDoc(taskRef, {
            ...task,
            id: firestoreId, // Update the ID to the Firestore document ID
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('✅ Task saved to Firestore successfully');
        // Return the updated task with the Firestore ID
        return { success: true, taskId: firestoreId };
    } catch (error) {
        console.error('❌ Error saving task to Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const updateTaskInFirestore = async (taskId, updates) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('💾 Updating task in Firestore:', taskId);

        const taskRef = doc(db, 'tasks', taskId.toString());
        await setDoc(taskRef, {
            ...updates,
            updatedAt: serverTimestamp()
        }, { merge: true });

        console.log('✅ Task updated in Firestore successfully');
        return { success: true };
    } catch (error) {
        console.error('❌ Error updating task in Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const updateProjectInFirestore = async (projectId, updates) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }
        const ref = doc(db, 'projects', String(projectId));
        await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
        return { success: true };
    } catch (error) {
        console.error('❌ updateProjectInFirestore:', error);
        return { success: false, error: error.message };
    }
};

export const saveProjectToFirestore = async (project) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        // Ensure user is authenticated with fresh token
        await ensureAuthenticated();

        console.log('💾 Saving project to Firestore:', project.id, 'for user:', userId);

        // Generate a proper Firestore document ID
        const projectRef = doc(collection(db, 'projects'));
        const firestoreId = projectRef.id;
        
        await setDoc(projectRef, {
            ...project,
            id: firestoreId, // Update the ID to the Firestore document ID
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('✅ Project saved to Firestore successfully');
        // Return the updated project with the Firestore ID
        return { success: true, projectId: firestoreId };
    } catch (error) {
        console.error('❌ Error saving project to Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const breakTaskIntoSubtasksAPI = async (taskId, taskText) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('🔧 Calling breakTaskIntoSubtasks function:', { taskId, taskText });
        const fn = httpsCallable(functions, 'breakTaskIntoSubtasks');
        const result = await fn({ taskId, taskText });
        console.log('✅ Subtasks result:', result.data);
        return result.data;
    } catch (error) {
        console.error('❌ Break task error:', error);
        return {
            success: false,
            error: error.message,
            needsAuth: !getCurrentUserId()
        };
    }
};

const OAUTH_PROVIDER_KEY = 'dendrite_oauth_provider';

export const setPendingOAuthProvider = (provider) => {
    try {
        if (provider === 'google' || provider === 'microsoft') {
            sessionStorage.setItem(OAUTH_PROVIDER_KEY, provider);
        }
    } catch (e) {
        /* ignore */
    }
};

/** After OAuth redirect, read which provider the user started (then clears the key). */
export const consumePendingOAuthProvider = () => {
    try {
        const v = sessionStorage.getItem(OAUTH_PROVIDER_KEY);
        sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
        if (v === 'microsoft' || v === 'google') {
            return v;
        }
    } catch (e) {
        /* ignore */
    }
    return null;
};

export const getGoogleDriveAuthUrlAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'getGoogleDriveAuthUrl');
    const result = await fn({});
    return result.data;
};

export const getMicrosoftAuthUrlAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'getMicrosoftAuthUrl');
    const result = await fn({});
    return result.data;
};

export const exchangeGoogleDriveCodeAPI = async ({ code, state }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'exchangeGoogleDriveCode');
    const result = await fn({ code, state });
    return result.data;
};

export const exchangeMicrosoftCodeAPI = async ({ code, state }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'exchangeMicrosoftCode');
    const result = await fn({ code, state });
    return result.data;
};

export const disconnectGoogleDriveAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'disconnectGoogleDrive');
    const result = await fn({});
    return result.data;
};

export const disconnectMicrosoftAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'disconnectMicrosoft');
    const result = await fn({});
    return result.data;
};

export const listGoogleDriveFilesAPI = async ({ q, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGoogleDriveFiles');
    const result = await fn({ q, pageToken, pageSize });
    return result.data;
};

export const importGoogleDriveFilesAPI = async ({ projectId, fileIds }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importGoogleDriveFiles');
    const result = await fn({ projectId, fileIds });
    return result.data;
};

export const listOneDriveFilesAPI = async ({ q, pageToken, pageSize, folderId } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listOneDriveFiles');
    const result = await fn({ q, pageToken, pageSize, folderId });
    return result.data;
};

export const importOneDriveFilesAPI = async ({ projectId, itemIds, driveId }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importOneDriveFiles');
    const result = await fn({ projectId, itemIds, driveId });
    return result.data;
};

export const searchSharePointSitesAPI = async ({ q }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'searchSharePointSites');
    const result = await fn({ q });
    return result.data;
};

export const listSharePointDrivesAPI = async ({ siteId }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listSharePointDrives');
    const result = await fn({ siteId });
    return result.data;
};

export const listGraphDriveItemsAPI = async ({ driveId, itemId, q, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGraphDriveItems');
    const result = await fn({ driveId, itemId, q, pageToken, pageSize });
    return result.data;
};

export const listOneNoteNotebooksAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listOneNoteNotebooks');
    const result = await fn({});
    return result.data;
};

export const listOneNoteSectionsAPI = async ({ notebookId }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listOneNoteSections');
    const result = await fn({ notebookId });
    return result.data;
};

export const listOneNotePagesAPI = async ({ sectionId, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listOneNotePages');
    const result = await fn({ sectionId, pageToken, pageSize });
    return result.data;
};

export const importOneNotePagesAPI = async ({ projectId, pageIds }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importOneNotePages');
    const result = await fn({ projectId, pageIds });
    return result.data;
};

export const listMicrosoftTeamsAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listMicrosoftTeams');
    const result = await fn({});
    return result.data;
};

export const listMicrosoftTeamChannelsAPI = async ({ teamId }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listMicrosoftTeamChannels');
    const result = await fn({ teamId });
    return result.data;
};

export const listTeamChannelMessagesAPI = async ({ teamId, channelId, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listTeamChannelMessages');
    const result = await fn({ teamId, channelId, pageToken, pageSize });
    return result.data;
};

export const importTeamChannelMessagesAPI = async ({ projectId, teamId, channelId, messageIds }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importTeamChannelMessages');
    const result = await fn({ projectId, teamId, channelId, messageIds });
    return result.data;
};

export const listMicrosoftChatsAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listMicrosoftChats');
    const result = await fn({});
    return result.data;
};

export const listChatMessagesAPI = async ({ chatId, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listChatMessages');
    const result = await fn({ chatId, pageToken, pageSize });
    return result.data;
};

export const importChatMessagesAPI = async ({ projectId, chatId, messageIds }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importChatMessages');
    const result = await fn({ projectId, chatId, messageIds });
    return result.data;
};

export const listGmailMessagesAPI = async ({ q, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGmailMessages');
    const result = await fn({ q, pageToken, pageSize });
    return result.data;
};

export const importGmailMessagesAPI = async ({ projectId, messageIds }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importGmailMessages');
    const result = await fn({ projectId, messageIds });
    return result.data;
};

const INTEGRATION_IMPORT_PROVIDERS = new Set([
    'googleDrive',
    'gmail',
    'googleChat',
    'googleTasks',
    'oneDrive',
    'sharePoint',
    'oneNote',
    'teamsChannel',
    'teamsChat'
]);

/** Live list of documents imported via Integrations (Drive, Gmail, Chat, Tasks). */
export const subscribeIntegrationImports = (userId, callback) => {
    if (!db || !userId) {
        callback([]);
        return () => {};
    }
    const q = query(
        collection(db, 'documents'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(150)
    );
    return onSnapshot(
        q,
        (snap) => {
            const rows = [];
            snap.forEach((d) => {
                const data = d.data();
                if (data.status !== 'imported') return;
                const prov = data.source && data.source.provider;
                if (!prov || !INTEGRATION_IMPORT_PROVIDERS.has(prov)) return;
                rows.push({ id: d.id, ...data });
            });
            callback(rows);
        },
        (err) => {
            console.error('subscribeIntegrationImports error:', err);
            callback([]);
        }
    );
};

export const listGoogleChatSpacesAPI = async ({ pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGoogleChatSpaces');
    const result = await fn({ pageToken, pageSize });
    return result.data;
};

export const listGoogleChatMessagesAPI = async ({ spaceName, pageToken, pageSize } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGoogleChatMessages');
    const result = await fn({ spaceName, pageToken, pageSize });
    return result.data;
};

export const importGoogleChatMessagesAPI = async ({ projectId, messageNames }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importGoogleChatMessages');
    const result = await fn({ projectId, messageNames });
    return result.data;
};

export const listGoogleTaskListsAPI = async () => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGoogleTaskLists');
    const result = await fn({});
    return result.data;
};

export const listGoogleTasksAPI = async ({ taskListId, showCompleted } = {}) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'listGoogleTasks');
    const result = await fn({ taskListId, showCompleted });
    return result.data;
};

export const importGoogleTasksAPI = async ({ projectId, taskListId, taskIds, taskListTitle }) => {
    const u = await getCallableUser();
    if (!u) {
        throw new Error('User must be authenticated');
    }
    const fn = httpsCallable(functions, 'importGoogleTasks');
    const result = await fn({ projectId, taskListId, taskIds, taskListTitle });
    return result.data;
};

/** Subscribes to the signed-in user's integration summary in Firestore (no tokens). */
export const subscribeUserIntegrations = (userId, callback) => {
    if (!db || !userId) {
        callback(null);
        return () => {};
    }
    const ref = doc(db, 'user_integrations', userId);
    return onSnapshot(
        ref,
        (snap) => {
            callback(snap.exists() ? snap.data() : null);
        },
        (err) => {
            console.error('user_integrations subscription error:', err);
            callback(null);
        }
    );
};

export const uploadDocumentAPI = async (fileName, content, fileSize, fileType) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('📄 Calling uploadDocument function:', { fileName, fileSize, fileType });
        const fn = httpsCallable(functions, 'uploadDocument');
        const result = await fn({ fileName, content, fileSize, fileType });
        console.log('✅ Upload result:', result.data);
        return result.data;
    } catch (error) {
        console.error('❌ Upload document error:', error);
        return {
            success: false,
            error: error.message,
            needsAuth: !getCurrentUserId()
        };
    }
};

// Test connection
export const testConnection = async () => {
    try {
        console.log('🧪 Testing Firebase connection...');

        const user = getCurrentUser();
        if (!user) {
            return {
                success: false,
                error: 'Not authenticated',
                needsAuth: true
            };
        }

        // Test Firestore connection
        const testQuery = query(
            collection(db, 'recordings'),
            where('userId', '==', user.uid),
            limit(1)
        );
        await getDocs(testQuery);

        console.log('✅ Firebase connection test successful');
        return {
            success: true,
            authenticated: true,
            user: {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName
            },
            projectId: firebaseConfig.projectId
        };
    } catch (error) {
        console.error('❌ Firebase connection test failed:', error);
        return { success: false, error: error.message };
    }
};

// Load user data
export const loadUserData = async () => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        console.log('📊 Loading user data from Firestore for user:', userId);

        // Load recordings
        const recordingsQuery = query(
            collection(db, 'recordings'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        // Load tasks
        const tasksQuery = query(
            collection(db, 'tasks'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        // Load documents
        const documentsQuery = query(
            collection(db, 'documents'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        // Load projects: equality on userId only, sort in client (avoids composite index / silent failure).
        const projectsQuery = query(
            collection(db, 'projects'),
            where('userId', '==', userId),
            limit(80)
        );

        console.log('🔍 Executing Firestore queries...');
        
        // Execute queries for existing collections
        const [recordingsSnapshot, tasksSnapshot, documentsSnapshot, projectsSnapshot] = await Promise.all([
            getDocs(recordingsQuery),
            getDocs(tasksQuery),
            getDocs(documentsQuery),
            getDocs(projectsQuery)
        ]);

        const recordings = [];
        const tasks = [];
        const documents = [];
        const projects = [];

        recordingsSnapshot.forEach(doc => {
            const data = doc.data();
            recordings.push({
                id: doc.id,
                ...data,
                timestamp: data.timestamp?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
            });
        });

        tasksSnapshot.forEach(doc => {
            const data = doc.data();
            tasks.push({
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
            });
        });

        documentsSnapshot.forEach(doc => {
            const data = doc.data();
            documents.push({
                id: doc.id,
                ...data,
                uploadDate: data.uploadDate?.toDate?.()?.toISOString() || data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
            });
        });

        projectsSnapshot.forEach((doc) => {
            const data = doc.data();
            projects.push({
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
            });
        });
        projects.sort((a, b) => {
            const ta = new Date(a.createdAt).getTime();
            const tb = new Date(b.createdAt).getTime();
            return tb - ta;
        });
        projects.splice(50);

        console.log('✅ Data loaded successfully:', {
            recordings: recordings.length,
            tasks: tasks.length,
            documents: documents.length,
            projects: projects.length
        });

        return {
            success: true,
            recordings,
            tasks,
            documents,
            projects
        };

    } catch (error) {
        console.error('❌ Error loading user data:', error);
        return {
            success: false,
            error: error.message,
            needsAuth: !getCurrentUserId(),
            recordings: [],
            tasks: [],
            documents: [],
            projects: []
        };
    }
};

const getChatSessionDocId = (userId, projectId) => {
    const projectKey = projectId && typeof projectId === 'string' && projectId.trim() !== '' && projectId !== 'all'
        ? projectId.split('/').join('_')
        : 'all';
    return `${userId}__${projectKey}`;
};

export const normalizeProjectIdForThread = (projectId) => {
    if (projectId && typeof projectId === 'string' && projectId !== 'all' && projectId.trim() !== '') {
        return projectId.trim();
    }
    return null;
};

const suggestedTitleFromMessages = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return 'New chat';
    const first = messages.find((m) => m && m.type === 'user' && m.content);
    if (!first?.content) return 'New chat';
    const t = String(first.content).replace(/\s+/g, ' ').trim();
    return t.length > 48 ? `${t.slice(0, 48)}…` : t;
};

const threadUpdatedAtMs = (v) => {
    if (v == null) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'object' && typeof v.seconds === 'number') {
        return v.seconds * 1000 + (v.nanoseconds || 0) / 1e6;
    }
    return 0;
};

export const loadChatThreads = async (userId, projectId) => {
    try {
        if (!userId) {
            return { success: false, error: 'User must be authenticated', threads: [] };
        }
        const p = normalizeProjectIdForThread(projectId);
        // Only equality on userId — filter archived, project, and sort in memory (no composite index).
        const q = query(
            collection(db, 'chat_threads'),
            where('userId', '==', userId),
            limit(200)
        );
        const snap = await getDocs(q);
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows = rows.filter((t) => t.archived !== true);
        rows = rows.filter((t) => t.kind !== 'capture_session');
        rows = rows.filter((t) => {
            if (p == null) {
                // Workspace: show all threads (with or without projectId)
                return true;
            }
            return t.projectId === p;
        });
        rows.sort((a, b) => threadUpdatedAtMs(b.updatedAt) - threadUpdatedAtMs(a.updatedAt));
        const threads = rows.slice(0, 50);
        return { success: true, threads };
    } catch (error) {
        console.error('❌ loadChatThreads:', error);
        return { success: false, error: error.message, threads: [] };
    }
};

export const createChatThread = async (userId, projectId) => {
    if (!userId) {
        return { success: false, error: 'User must be authenticated' };
    }
    const p = normalizeProjectIdForThread(projectId);
    const ref = await addDoc(collection(db, 'chat_threads'), {
        userId,
        projectId: p,
        title: 'New chat',
        userEditedTitle: false,
        messages: [],
        archived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return { success: true, id: ref.id };
};

export const createCaptureSessionThread = async (userId, projectId, { templateId, title } = {}) => {
    if (!userId) {
        return { success: false, error: 'User must be authenticated' };
    }
    const p = normalizeProjectIdForThread(projectId);
    const ref = await addDoc(collection(db, 'chat_threads'), {
        userId,
        projectId: p,
        kind: 'capture_session',
        templateId: templateId || null,
        title: (title && String(title).trim()) || 'Capture session',
        userEditedTitle: true,
        messages: [],
        archived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
    return { success: true, id: ref.id };
};

export const loadCaptureSessions = async (userId, projectId) => {
    try {
        if (!userId) {
            return { success: false, error: 'User must be authenticated', sessions: [] };
        }
        const p = normalizeProjectIdForThread(projectId);
        const q = query(collection(db, 'chat_threads'), where('userId', '==', userId), limit(200));
        const snap = await getDocs(q);
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows = rows.filter((t) => t.archived !== true && t.kind === 'capture_session');
        rows = rows.filter((t) => {
            if (p == null) return true;
            return t.projectId === p;
        });
        rows.sort((a, b) => threadUpdatedAtMs(b.updatedAt) - threadUpdatedAtMs(a.updatedAt));
        return { success: true, sessions: rows.slice(0, 30) };
    } catch (error) {
        console.error('loadCaptureSessions:', error);
        return { success: false, error: error.message, sessions: [] };
    }
};

export const loadChatThreadById = async (threadId) => {
    try {
        const r = doc(db, 'chat_threads', threadId);
        const snap = await getDoc(r);
        if (!snap.exists()) {
            return { success: false, error: 'Not found', messages: [] };
        }
        const d = snap.data();
        if (d.archived && d.kind !== 'capture_session') {
            return { success: false, error: 'Archived', messages: [] };
        }
        return {
            success: true,
            thread: { id: snap.id, ...d },
            messages: Array.isArray(d.messages) ? d.messages : []
        };
    } catch (error) {
        console.error('❌ loadChatThreadById:', error);
        return { success: false, error: error.message, messages: [] };
    }
};

export const saveChatThreadMessages = async (userId, threadId, messages) => {
    if (!userId || !threadId) {
        throw new Error('userId and threadId required');
    }
    const threadRef = doc(db, 'chat_threads', threadId);
    const existing = await getDoc(threadRef);
    const userKept = existing.exists() && existing.data().userEditedTitle === true;
    const list = Array.isArray(messages) ? messages.slice(-200) : [];
    const payload = {
        messages: list,
        updatedAt: serverTimestamp()
    };
    if (!userKept) {
        payload.title = suggestedTitleFromMessages(list);
    }
    await updateDoc(threadRef, payload);
    return { success: true };
};

export const updateChatThreadTitle = async (userId, threadId, newTitle) => {
    if (!userId || !threadId) {
        throw new Error('userId and threadId required');
    }
    const t = (newTitle && String(newTitle).trim()) || 'New chat';
    const threadRef = doc(db, 'chat_threads', threadId);
    await updateDoc(threadRef, {
        title: t.length > 80 ? t.slice(0, 80) : t,
        userEditedTitle: true,
        updatedAt: serverTimestamp()
    });
    return { success: true };
};

export const archiveChatThread = async (userId, threadId) => {
    if (!userId || !threadId) {
        throw new Error('userId and threadId required');
    }
    const threadRef = doc(db, 'chat_threads', threadId);
    await updateDoc(threadRef, {
        archived: true,
        updatedAt: serverTimestamp()
    });
    return { success: true };
};

export const updateCaptureSessionMetadata = async (userId, threadId, fields) => {
    if (!userId || !threadId) {
        throw new Error('userId and threadId required');
    }
    const threadRef = doc(db, 'chat_threads', threadId);
    await updateDoc(threadRef, {
        ...fields,
        updatedAt: serverTimestamp()
    });
    return { success: true };
};

/**
 * If no threads exist for this scope, copy legacy chat_sessions (single doc per user+project) once.
 */
export const migrateLegacyChatSessionIfEmpty = async (userId, projectId) => {
    if (!userId) return { migrated: false };
    const threadResult = await loadChatThreads(userId, projectId);
    if (threadResult.success && threadResult.threads && threadResult.threads.length > 0) {
        return { migrated: false };
    }
    const legacy = await loadChatMessagesFromFirestore(userId, projectId);
    if (!legacy.success || !Array.isArray(legacy.messages) || legacy.messages.length === 0) {
        return { migrated: false };
    }
    const created = await createChatThread(userId, projectId);
    if (!created.success || !created.id) {
        return { migrated: false };
    }
    try {
        await saveChatThreadMessages(userId, created.id, legacy.messages);
    } catch (e) {
        console.warn('Migration save failed', e);
    }
    return { migrated: true, threadId: created.id };
};

export const loadChatMessagesFromFirestore = async (userId, projectId) => {
    try {
        if (!userId) {
            return { success: false, error: 'User must be authenticated', messages: [] };
        }

        const sessionId = getChatSessionDocId(userId, projectId);
        const sessionRef = doc(db, 'chat_sessions', sessionId);
        const snap = await getDoc(sessionRef);

        if (!snap.exists()) {
            return { success: true, messages: [] };
        }

        const data = snap.data();
        const messages = Array.isArray(data.messages) ? data.messages : [];
        return { success: true, messages };
    } catch (error) {
        console.error('❌ Error loading chat messages:', error);
        return { success: false, error: error.message, messages: [] };
    }
};

export const saveChatMessagesToFirestore = async (userId, projectId, messages) => {
    try {
        if (!userId) {
            throw new Error('User must be authenticated');
        }

        const sessionId = getChatSessionDocId(userId, projectId);
        const sessionRef = doc(db, 'chat_sessions', sessionId);

        await setDoc(sessionRef, {
            userId,
            projectId: projectId && projectId !== 'all' ? projectId : null,
            messages: messages.slice(-200),
            updatedAt: serverTimestamp()
        }, { merge: true });

        return { success: true };
    } catch (error) {
        console.error('❌ Error saving chat messages:', error);
        return { success: false, error: error.message };
    }
};

// Delete functions for Firestore persistence
export const deleteTaskFromFirestore = async (taskId) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User not authenticated');
        }

        console.log('🗑️ Deleting task from Firestore:', taskId, 'for user:', userId);
        const taskRef = doc(db, 'tasks', taskId);
        await deleteDoc(taskRef);
        
        console.log('✅ Task deleted successfully from Firestore');
        return { success: true };
    } catch (error) {
        console.error('❌ Error deleting task from Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const deleteRecordingFromFirestore = async (recordingId) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User not authenticated');
        }

        console.log('🗑️ Deleting recording from Firestore:', recordingId, 'for user:', userId);
        const recordingRef = doc(db, 'recordings', recordingId);
        await deleteDoc(recordingRef);
        
        console.log('✅ Recording deleted successfully from Firestore');
        return { success: true };
    } catch (error) {
        console.error('❌ Error deleting recording from Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const deleteDocumentFromFirestore = async (documentId) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User not authenticated');
        }

        console.log('🗑️ Deleting document from Firestore:', documentId, 'for user:', userId);
        const documentRef = doc(db, 'documents', documentId);
        await deleteDoc(documentRef);
        
        console.log('✅ Document deleted successfully from Firestore');
        return { success: true };
    } catch (error) {
        console.error('❌ Error deleting document from Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const deleteProjectFromFirestore = async (projectId) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User not authenticated');
        }

        console.log('🗑️ Deleting project from Firestore:', projectId, 'for user:', userId);
        const projectRef = doc(db, 'projects', projectId);
        await deleteDoc(projectRef);
        
        console.log('✅ Project deleted successfully from Firestore');
        return { success: true };
    } catch (error) {
        console.error('❌ Error deleting project from Firestore:', error);
        return { success: false, error: error.message };
    }
};

export const updateDocumentInFirestore = async (documentId, updates) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }
        const ref = doc(db, 'documents', String(documentId));
        await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
        return { success: true };
    } catch (error) {
        console.error('❌ updateDocumentInFirestore:', error);
        return { success: false, error: error.message };
    }
};

export const updateRecordingInFirestore = async (recordingId, updates) => {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User must be authenticated');
        }
        const ref = doc(db, 'recordings', String(recordingId));
        await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
        return { success: true };
    } catch (error) {
        console.error('❌ updateRecordingInFirestore:', error);
        return { success: false, error: error.message };
    }
};

export { db, auth, storage, functions };
export default app;